import type { ProductVariant } from '~~/types/commercetools';

import extendProducts from '~~/helpers/product/extendProducts';
import { getProductSearchParams } from '~~/helpers/product/getProductSearchParams';
import { ALTERNATIVE_PRODUCTS_FETCH_LIMIT } from '~~/constants/alternativeProducts';
import { getAlternativeProductFilter } from '~~/helpers/commercetools/graphql/getAlternativeProductFilter';
import type { AlternativeProductData, AlternativeProductResponse } from '~~/types/product/AlternativeProducts';
import {
  getProductAttributesExtended,
} from '~~/helpers/product/getProductAttributesExtended';
import { PRODUCT_ATTRIBUTES } from '~~/constants/products';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import type { ProductVariantGql } from '~~/types/product/ProductVariantGql';

export default function (cacheId: string) {

  const { languagePrefix } = usePrefix();
  const { languageAndCountry, countryCode } = useLocalization();
  const { combineTechnicalDetails } = useProductDetailsCombined();
  const { makeCommercetoolsRequest } = useExpondoMiddleware();

  const productsData = useState<AlternativeProductData[]>(`${cacheId}-alternativeProducts`, () => []);

  const search = async (product: ProductVariant) => {

    productsData.value = [];
    const alternativeProductsFilter = getAlternativeProductFilter(product);

    if (!alternativeProductsFilter) {

      return;
    
    }

    const productSearchParams = getProductSearchParams(
      {
        limit: ALTERNATIVE_PRODUCTS_FETCH_LIMIT,
        customFilters: alternativeProductsFilter,
        sku: product.sku,
      },
      { languageAndCountry: languageAndCountry.value, languagePrefix, isOnStock: true },
    );

    const products = await makeCommercetoolsRequest<AlternativeProductResponse[]>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_ALTERNATIVE_PRODUCTS,
      data: productSearchParams,
    });

    const productsWithFlags = products?.data || [];

    // Extract the full product objects
    const productObjects = productsWithFlags.map(item => item.product).filter(Boolean) as ProductVariantGql[];

    const extendedProducts = extendProducts(
      productObjects,
      languageAndCountry.value,
      countryCode.value,
      { isPublished: true },
    );

    const data = extendedProducts.map((product, index) => {

      const attributesLocalized = getProductAttributesExtended(
        product,
        {
          attributeNames: [PRODUCT_ATTRIBUTES.TECH_DETAILS],
          languageAndCountry: languageAndCountry.value,
        },
      );
      combineTechnicalDetails(product, attributesLocalized);

      return {
        product,
        attributesLocalized,
        isBwareVariant: productsWithFlags[index]?.isBwareVariant || false,
      };
    
    });
    productsData.value = data;
  
  };

  return {
    productsData,
    search,
  };

}
